<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <h1 class="title">List of products</h1>
        <app-button
            v-if="is_general_manager || is_product_storage_supervisor"
            @click.native="onExport"
            theme="export"
            :class="{'btn--export--loading': export_loading}"
        >
          <img src="@/assets/img/export-icon.svg" alt="Export">
          <span>Export in .xlsx</span>
        </app-button>
      </app-cells>
      <app-cells>
        <app-button
            v-if="is_general_manager || is_product_storage_supervisor"
            @click.native="$modal.show('create-product')"
        >
          Create product
        </app-button>
        <app-input
            @input.native="onProductSearch"
            v-model="search"
            class="input--300"
            type="search"
            placeholder="Search by product name"
            name="search"
        />
        <app-select
          @input="onFilterProducts"
          v-model="filter_category"
          :reduce="item => item.id"
          :options="category_options"
          placeholder="Category"
          class="select--300"
        />
        <app-select
          @input="onFilterProducts"
          v-model="filter_suppliers"
          :options="supplier_options"
          :reduce="item => item.id"
          placeholder="Supplier"
          class="select--300"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'product-storage-products-detail', params: { id: row.id } }"
              class="table-default__link-border"
            >
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #price="{ row }">{{ row.price.toLocaleString() }} TSh</template>
        <template #recommended_balance="{ row }">{{ row.recommended_balance.toLocaleString() }}</template>
        <template #in_stock="{ row }">{{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 1}).format(row.in_stock) }}</template>
        <template #status="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color || '#8a8a8a' }">
            {{ row.status.name }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
        name="create-product"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create-product')" class="popup__close" type="button"></button>
        <div class="popup__title">New product</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Name" label-for="product_name" required>
                  <app-input
                      v-model="form.name"
                      @change.native="$v.form.name.$touch()"
                      :error="$v.form.name.$error"
                      id="product_name"
                      placeholder="Product name"
                      name="product_name"
                  />
                  <template #error>
                    <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Category" required>
                  <app-select
                    v-model="form.category"
                    :reduce="item => item.id"
                    :options="category_options"
                    :clearable="false"
                    placeholder="Category"
                    :error="$v.form.category.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.category.$dirty && !$v.form.category.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Measurement" required>
                  <app-select
                    v-model="form.measurement"
                    :reduce="item => item.id"
                    :options="measurement_options"
                    :clearable="false"
                    placeholder="Measurement"
                    :error="$v.form.measurement.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.measurement.$dirty && !$v.form.measurement.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Recomm. balance" label-for="recommended_balance" required>
                  <app-input
                      v-model="form.recommended_balance"
                      @change.native="$v.form.recommended_balance.$touch()"
                      :error="$v.form.recommended_balance.$error"
                      id="recommended_balance"
                      placeholder="Recomm. balance"
                      type="number"
                      name="recommended_balance"
                  />
                  <template #error>
                    <div v-if="$v.form.recommended_balance.$dirty && !$v.form.recommended_balance.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Last price (TSh)" label-for="price" required>
                  <app-input
                      v-model="form.price"
                      @change.native="$v.form.price.$touch()"
                      :error="$v.form.price.$error"
                      id="price"
                      placeholder="Last price (TSh)"
                      type="number"
                      name="price"
                  />
                  <template #error>
                    <div v-if="$v.form.price.$dirty && !$v.form.price.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Supplier" required>
                  <app-select
                    v-model="form.suppliers"
                    :options="suppliers_options"
                    :clearable="false"
                    :closeOnSelect="false"
                    placeholder="Supplier"
                    multiple
                    :error="$v.form.suppliers.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.suppliers.$dirty && !$v.form.suppliers.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create product</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  geMeasurements,
  getProductcategory,
  getProductsExport,
  getProductsProductstorage,
  getSuppliersProduct,
  postProductStorageProduct,
} from '@/http'
import { format, parse } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'ProductStorageProductList',
  data() {
    return {
      is_loaded: false,
      export_loading: false,
      table: {
        items: [],
        columns: ['name', 'measurement.name', 'price', 'recommended_balance', 'in_stock', 'status'],
        options: {
          headings: {
            'name': 'Name',
            'measurement.name': 'Unit',
            'price': 'Last price',
            'recommended_balance': 'Recomm. balance',
            'in_stock': 'Current stock',
            'status': 'Status',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {},
      category_options: [],
      measurement_options: [],
      supplier_options: [],
      suppliers_options: [],
      search: null,
      filter_category: null,
      filter_suppliers: null,
      table_preloader: false
    }
  },
  validations: {
    form: {
      name: { required },
      category: { required },
      measurement: { required },
      recommended_balance: { required },
      price: { required },
      suppliers: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_supervisor')
    },
  },
  created() {
    this.fetchItems()
    getProductcategory().then(res => { this.category_options = res.data })
    geMeasurements().then(res => { this.measurement_options = res.data })
    getSuppliersProduct().then(response => {
      this.suppliers_options = response.data
      this.supplier_options = response.data
    })
  },
  methods: {
    onExport() {
      this.export_loading = true
      getProductsExport()
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Altezza Product storage products ${format(new Date(), 'dd.MM.yyyy HH_mm')}.xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.export_loading = false
        })
    },
    onFilterProducts() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    fetchItems() {
      getProductsProductstorage({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        category: this.search ? '' : this.filter_category,
        suppliers: this.search ? '' : this.filter_suppliers
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postProductStorageProduct(this.normalizeForm())
        .finally(() => { this.$refs.submit.preload = false })
        .then(() => {
          this.fetchItems()
          this.form = {}
          this.$v.form.$reset()
          this.$notify({
            type: 'success',
            title: 'Product created',
          })
          this.$modal.hide('create-product')
        })
    },
    normalizeForm() {
      const form = {...this.form}

      form.suppliers = form.suppliers.map(item => item.id)

      return form
    },
    onProductSearch() {
      this.searchProduct(this)
    },
    searchProduct: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
